/**
 * Target Compliance Checker - Validates bond series against target constraints
 *
 * This module ensures that targeted bond scenarios meet their principal constraints.
 * It implements the second tier of viability checking (after DRF balance checks).
 *
 * Target Types:
 * - DOES_NOT_EXCEED: Bond principal must not exceed the target (maximum constraint)
 * - ACCEPTS_OVERFLOW: Bond principal should meet or exceed the target (overflow recipient)
 */

import {
  calculateTargetExcess,
  calculateTargetShortfall,
} from '~/engine/processing/calculators/validation/target-validation-calculator';
import { logger } from '~/services/logging';
import type { BondSeries, Candidate, Configuration } from '~/types';
import { TargetType } from '~/types';

export class TargetComplianceChecker {
  /**
   * Check if candidate meets all bond target constraints
   * Updates candidate.results.isViable and adds failure messages if targets are not met
   *
   * @param candidate - The candidate to check for target compliance
   * @param configuration - Configuration containing bond series
   * @returns void - Updates candidate.results in place
   */
  static checkTargetCompliance(candidate: Candidate, configuration: Configuration): void {
    const bondSeries = configuration.bondSeries;
    const { cashFlows, results } = candidate;

    // Only check if candidate is currently viable
    if (!results.isViable) {
      logger.L4.debug(`⏭️ Skipping target compliance for ${candidate.name} - already non-viable`);
      return;
    }

    // Check if any bond series has targets
    const bondsWithTargets = bondSeries.filter((bs) => bs.targetInformation !== undefined);
    if (bondsWithTargets.length === 0) {
      logger.L4.debug(`⏭️ No targeted bonds for ${candidate.name}`);
      return;
    }

    logger.L4.debug(
      `🎯 Checking target compliance for ${candidate.name} (${bondsWithTargets.length} targeted bonds)`
    );

    const targetViolations: string[] = [];

    for (const bond of bondsWithTargets) {
      const targetInfo = bond.targetInformation!;

      // Calculate current principal for this bond series
      const currentPrincipal = cashFlows.reduce((total, cf) => {
        const bondPayment = cf.bondPayments.find((bp) => bp.bondSeriesId === bond.id);
        return total + (bondPayment?.principal || 0);
      }, 0);

      logger.L4.debug(
        `  Bond ${bond.name}: Principal=$${currentPrincipal.toLocaleString()}, Target=$${targetInfo.principalTargetAmount.toLocaleString()} (${targetInfo.targetType})`,
        { context: 'targetCompliance' }
      );

      // Check target compliance based on target type
      if (targetInfo.targetType === TargetType.DOES_NOT_EXCEED) {
        // DOES_NOT_EXCEED means the bond MUST hit the target exactly (cannot go over, must reach it)
        if (currentPrincipal > targetInfo.principalTargetAmount) {
          const message = `Bond ${bond.name} exceeds target: $${currentPrincipal.toLocaleString()} > $${targetInfo.principalTargetAmount.toLocaleString()}`;
          targetViolations.push(message);
          logger.L4.debug(`❌ ${message}`);
        } else if (currentPrincipal < targetInfo.principalTargetAmount) {
          const shortfall = calculateTargetShortfall(
            targetInfo.principalTargetAmount,
            currentPrincipal
          );
          const message = `Bond ${bond.name} fails to reach target: $${currentPrincipal.toLocaleString()} < $${targetInfo.principalTargetAmount.toLocaleString()} (shortfall: $${shortfall.toLocaleString()})`;
          targetViolations.push(message);
          logger.L4.debug(`❌ ${message}`);
        } else {
          logger.L4.debug(
            `✅ Bond ${bond.name} meets target exactly: $${currentPrincipal.toLocaleString()}`
          );
        }
      } else if (targetInfo.targetType === TargetType.ACCEPTS_OVERFLOW) {
        if (currentPrincipal < targetInfo.principalTargetAmount) {
          const message = `Bond ${bond.name} does not meet target: $${currentPrincipal.toLocaleString()} < $${targetInfo.principalTargetAmount.toLocaleString()}`;
          targetViolations.push(message);
          logger.L4.debug(`❌ ${message}`);
        } else {
          logger.L4.debug(
            `✅ Bond ${bond.name} meets target: $${currentPrincipal.toLocaleString()} ≥ $${targetInfo.principalTargetAmount.toLocaleString()}`
          );
        }
      }
    }

    // Mark as non-viable if targets are not met
    if (targetViolations.length > 0) {
      results.isViable = false;
      if (!results.failureMessages) {
        results.failureMessages = [];
      }
      results.failureMessages.push(...targetViolations);

      logger.L4.debug(
        `❌ Target compliance FAILED for ${candidate.name}: ${targetViolations.length} violations`
      );
    } else {
      logger.L4.debug(`✅ Target compliance PASSED for ${candidate.name}`);
    }
  }

  /**
   * Get target achievement status for a specific bond series
   * Useful for reporting and UI display
   *
   * @param bond - The bond series to check
   * @param currentPrincipal - The actual principal amount distributed to this bond
   * @returns Object with achievement status and any shortfall/excess
   */
  static getBondTargetStatus(
    bond: BondSeries,
    currentPrincipal: number
  ): {
    hasTarget: boolean;
    targetAchieved: boolean;
    targetShortfall: number;
    targetExcess: number;
    complianceMessage: string;
  } {
    if (!bond.targetInformation) {
      return {
        hasTarget: false,
        targetAchieved: true,
        targetShortfall: 0,
        targetExcess: 0,
        complianceMessage: 'No target',
      };
    }

    const targetInfo = bond.targetInformation;
    const targetAmount = targetInfo.principalTargetAmount;

    if (targetInfo.targetType === TargetType.DOES_NOT_EXCEED) {
      // DOES_NOT_EXCEED means the bond MUST hit the target exactly
      const shortfall = calculateTargetShortfall(targetAmount, currentPrincipal);
      const excess = calculateTargetExcess(currentPrincipal, targetAmount);

      let targetAchieved = false;
      let complianceMessage = '';

      if (currentPrincipal > targetAmount) {
        targetAchieved = false;
        complianceMessage = `Exceeds target by $${excess.toLocaleString()}`;
      } else if (currentPrincipal < targetAmount) {
        targetAchieved = false;
        complianceMessage = `Short of target by $${Math.abs(shortfall).toLocaleString()}`;
      } else {
        targetAchieved = true;
        complianceMessage = `Target met exactly: $${currentPrincipal.toLocaleString()}`;
      }

      return {
        hasTarget: true,
        targetAchieved,
        targetShortfall: Math.max(0, shortfall),
        targetExcess: Math.max(0, excess),
        complianceMessage,
      };
    } else {
      // ACCEPTS_OVERFLOW
      const targetAchieved = currentPrincipal >= targetAmount;
      const shortfall = calculateTargetShortfall(targetAmount, currentPrincipal);

      return {
        hasTarget: true,
        targetAchieved,
        targetShortfall: shortfall,
        targetExcess: 0,
        complianceMessage: targetAchieved
          ? `Target met ($${currentPrincipal.toLocaleString()} ≥ $${targetAmount.toLocaleString()})`
          : `Short by $${shortfall.toLocaleString()}`,
      };
    }
  }

  /**
   * Check if any bonds in the candidate have targets
   * Quick check to determine if target compliance checking is needed
   *
   * @param candidate - The candidate to check
   * @returns true if any bond series has target information
   */
  static hasTargetedBonds(_candidate: Candidate, configuration: Configuration): boolean {
    return configuration.bondSeries.some((bs) => bs.targetInformation !== undefined);
  }
}
